<template>
  <div>
    <StaffNavbar />
    <AlertBox ref="alertBox" />

    <div class="report-page screen-control">
      <section class="report-hero shadow">
        <div class="row g-4 align-items-center">
          <div class="col-12 col-lg">
            <p class="hero-eyebrow mb-2">เมนูรายงาน</p>
            <h1 class="hero-title mb-2">รายงานคำขอกู้ยืมจากข้อมูลที่นักศึกษายื่นจริง</h1>
            <p class="hero-subtitle mb-0">
              แสดงสรุปรายการพร้อมเปิดดูข้อมูลคำขอในรูปแบบเดียวกับเอกสาร PDF
              เพื่อให้ staff ตรวจสอบได้จากหน้า report ทันที
            </p>
          </div>

          <div class="col-12 col-lg-auto">
            <div class="hero-highlight">
              <div class="hero-highlight__value">{{ reportItems.length }}</div>
              <div class="hero-highlight__label">รายการที่ตรงกับเงื่อนไข</div>
            </div>
          </div>
        </div>
      </section>

      <section class="report-panel shadow">
        <div class="panel-heading">
          <div>
            <h5 class="mb-1">ตัวกรองรายงาน</h5>
            <p class="text-muted mb-0">ค้นหาตามข้อมูลการยื่นกู้และสถานะล่าสุดของนักศึกษา</p>
          </div>
          <span class="filter-badge">ใช้งานอยู่ {{ activeFiltersCount }} ตัวกรอง</span>
        </div>

        <div class="row g-3">
          <div class="col-12 col-md-6 col-xl-3">
            <label class="form-label">รหัสนักศึกษา</label>
            <input
              v-model.trim="filters.student_id"
              type="text"
              class="form-control"
              placeholder="กรอกรหัสนักศึกษา"
            />
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <label class="form-label">ชื่อ</label>
            <input
              v-model.trim="filters.first_name"
              type="text"
              class="form-control"
              placeholder="กรอกชื่อ"
            />
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <label class="form-label">นามสกุล</label>
            <input
              v-model.trim="filters.last_name"
              type="text"
              class="form-control"
              placeholder="กรอกนามสกุล"
            />
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <label class="form-label">ระดับการศึกษา</label>
            <select v-model="filters.graduated_level" class="form-select">
              <option value="">ทั้งหมด</option>
              <option value="ปริญญาตรี">ปริญญาตรี</option>
              <option value="ปริญญาโท">ปริญญาโท</option>
              <option value="ประกาศนียบัตร">ประกาศนียบัตร</option>
            </select>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <label class="form-label">ปีการศึกษา</label>
            <select v-model="filters.study_year" class="form-select">
              <option value="">ทั้งหมด</option>
              <option v-for="year in studyYears" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <label class="form-label">ภาคเรียน</label>
            <select v-model="filters.study_semester" class="form-select">
              <option value="">ทั้งหมด</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <label class="form-label">สถานะคำขอ</label>
            <select v-model="filters.loan_status" class="form-select">
              <option value="">ทั้งหมด</option>
              <option v-for="status in statusOptions" :key="status" :value="status">
                {{ status }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-6 col-xl-3 d-flex align-items-end gap-2">
            <button
              class="btn btn-report-primary flex-fill"
              :disabled="isLoading"
              @click="loadReports"
            >
              <i class="fa-solid fa-magnifying-glass me-2"></i>ค้นหา
            </button>
            <button class="btn btn-report-light" :disabled="isLoading" @click="clearFilters">
              ล้างค่า
            </button>
          </div>
        </div>
      </section>

      <section class="row g-3 mb-4">
        <div class="col-12 col-md-6 col-xl-3" v-for="stat in stats" :key="stat.label">
          <div class="stat-card shadow-sm">
            <div class="stat-card__label">{{ stat.label }}</div>
            <div class="stat-card__value">{{ stat.value }}</div>
            <div class="stat-card__helper">{{ stat.helper }}</div>
          </div>
        </div>
      </section>

      <section class="report-panel shadow">
        <div class="panel-heading align-items-start">
          <div>
            <h5 class="mb-1">สรุปรายการแบบคำขอกู้ยืม</h5>
            <p class="text-muted mb-0">
              แสดงเฉพาะสรุปรายการ และเปิดรายงานฉบับเต็มได้จากปุ่มด้านล่าง
            </p>
            <p class="text-muted mb-0 mt-1" v-if="filteredReportItems.length">
              แสดง {{ paginatedReportItems.length }} จาก {{ filteredReportItems.length }} รายการ
              <span v-if="selectedFaculty !== 'ทั้งหมด'">
                ในสำนักวิชา <b>{{ selectedFaculty }}</b>
              </span>
            </p>
          </div>

          <div class="faculty-dropdown" v-if="facultyOptions.length">
            <label class="form-label mb-1">เลือกสำนักวิชา</label>
            <select
              v-model="selectedFaculty"
              class="form-select"
              @change="selectFaculty(selectedFaculty)"
            >
              <option value="ทั้งหมด">ทั้งหมด {{ reportItems.length }} รายการ</option>
              <option
                v-for="faculty in facultyOptions"
                :key="faculty.name"
                :value="faculty.name"
              >
                {{ faculty.name }} {{ faculty.count }} รายการ
              </option>
            </select>
          </div>
        </div>

        <div v-if="isLoading" class="loading-state">
          <div class="spinner-border text-danger" role="status"></div>
        </div>

        <div v-else-if="!filteredReportItems.length" class="empty-state">
          <i class="fa-regular fa-folder-open"></i>
          <h5 class="mt-3 mb-2">ไม่พบข้อมูลตามเงื่อนไข</h5>
          <p class="text-muted mb-0">ลองเปลี่ยนตัวกรองแล้วค้นหาใหม่อีกครั้ง</p>
        </div>

        <div v-else class="report-list">
          <details
            v-for="item in paginatedReportItems"
            :key="item.public_id"
            class="report-card shadow-sm report-dropdown"
          >
            <summary class="report-summary">
              <div class="report-summary__main">
                <div class="report-card__title">
                  {{ item.user_username }} {{ item.user_surname }}
                </div>
                <div class="report-card__subtitle">
                  รหัสนักศึกษา {{ item.user_studentID }}
                </div>
                <div class="report-summary__meta">
                  {{ item.date || "-" }} {{ item.time || "" }}
                  <span class="report-summary__faculty">
                    {{ item.user_lvlStudy || "-" }}
                  </span>
                  <span class="report-summary__faculty">
                    {{ item.user_faculty || "ไม่ระบุสำนักวิชา" }}
                  </span>
                </div>
              </div>

              <div class="report-summary__actions">
                <span :class="['status-pill', statusClass(item.loan_status)]">
                  {{ item.loan_status || "-" }}
                </span>
                <span class="report-summary__icon">
                  <i class="fa-solid fa-chevron-down"></i>
                </span>
              </div>
            </summary>

            <div class="report-dropdown__content">
              <div class="report-card__footer">
                <button
                  class="btn btn-report-primary btn-sm report-card__view-button"
                  @click="openFullReport(item.public_id)"
                >
                  <i class="fa-solid fa-eye"></i>
                  <span>ดูรายงานฉบับเต็ม</span>
                </button>
              </div>
            </div>
          </details>

          <div class="pagination-bar" v-if="totalPages > 1">
            <button
              type="button"
              class="btn btn-report-light"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              ก่อนหน้า
            </button>

            <div class="pagination-pages">
              <button
                v-for="page in visiblePages"
                :key="page"
                type="button"
                class="pagination-page"
                :class="{ 'pagination-page--active': currentPage === page }"
                @click="changePage(page)"
              >
                {{ page }}
              </button>
            </div>

            <button
              type="button"
              class="btn btn-report-light"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              ถัดไป
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import StaffNavbar from "./NavBar_staff.vue";
import AlertBox from "../AlertBox.vue";

export default {
  name: "StaffPage_report",
  components: {
    StaffNavbar,
    AlertBox,
  },
  data() {
    return {
      isLoading: false,
      reportItems: [],
      facultyMasterList: [],
      selectedFaculty: "ทั้งหมด",
      currentPage: 1,
      itemsPerPage: 10,
      filters: {
        student_id: "",
        first_name: "",
        last_name: "",
        graduated_level: "",
        study_year: "",
        study_semester: "",
        loan_status: "",
      },
      studyYears: ["2564", "2565", "2566", "2567", "2568", "2569"],
      statusOptions: [
        "อนุมัติ",
        "ผ่านการตรวจสอบคุณสมบัติ",
        "อยู่ระหว่างดำเนินการ",
        "ตีกลับเพื่อแก้เอกสาร",
        "อยู่ระหว่างตรวจสอบคุณสมบัติผู้กู้ยืม",
        "รอผลการพิจารณาจากกองทุน กยศ.",
        "ไม่อนุมัติ",
      ],
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL || "",
    };
  },
  computed: {
    activeFiltersCount() {
      return Object.values(this.filters).filter((value) => String(value).trim() !== "").length;
    },
    filteredReportItems() {
      if (this.selectedFaculty === "ทั้งหมด") {
        return this.reportItems;
      }

      return this.reportItems.filter(
        (item) => (item.user_faculty || "ไม่ระบุสำนักวิชา") === this.selectedFaculty
      );
    },
    paginatedReportItems() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredReportItems.slice(startIndex, startIndex + this.itemsPerPage);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredReportItems.length / this.itemsPerPage));
    },
    visiblePages() {
      const total = this.totalPages;
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(total, start + 4);
      const normalizedStart = Math.max(1, end - 4);
      const pages = [];

      for (let page = normalizedStart; page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
    stats() {
      const total = this.reportItems.length;
      const approved = this.reportItems.filter((item) =>
        ["อนุมัติ", "ผ่านการตรวจสอบคุณสมบัติ"].includes(item.loan_status)
      ).length;
      const inProgress = this.reportItems.filter((item) =>
        [
          "อยู่ระหว่างดำเนินการ",
          "อยู่ระหว่างตรวจสอบคุณสมบัติผู้กู้ยืม",
          "รอผลการพิจารณาจากกองทุน กยศ.",
        ].includes(item.loan_status)
      ).length;
      const needsAction = this.reportItems.filter((item) =>
        ["ตีกลับเพื่อแก้เอกสาร", "ไม่อนุมัติ"].includes(item.loan_status)
      ).length;

      return [
        {
          label: "คำขอทั้งหมด",
          value: total,
          helper: "จำนวนรายการที่ตรงกับตัวกรอง",
        },
        {
          label: "ผ่านแล้ว",
          value: approved,
          helper: "อนุมัติหรือผ่านการตรวจสอบคุณสมบัติ",
        },
        {
          label: "กำลังดำเนินการ",
          value: inProgress,
          helper: "ยังอยู่ในขั้นตอนตรวจสอบหรือรอผล",
        },
        {
          label: "ต้องติดตาม",
          value: needsAction,
          helper: "ตีกลับเพื่อแก้เอกสารหรือไม่อนุมัติ",
        },
      ];
    },
    facultyOptions() {
      const grouped = this.reportItems.reduce((accumulator, item) => {
        const key = item.user_faculty || "ไม่ระบุสำนักวิชา";
        accumulator[key] = (accumulator[key] || 0) + 1;
        return accumulator;
      }, {});

      const masterList = this.facultyMasterList.filter(
        (faculty) => faculty && faculty !== "ไม่ระบุสำนักวิชา"
      );
      const knownFaculties = new Set(masterList);
      const options = masterList.map((name) => ({
        name,
        count: grouped[name] || 0,
      }));

      Object.entries(grouped).forEach(([name, count]) => {
        if (!knownFaculties.has(name)) {
          options.push({ name, count });
        }
      });

      return options;
    },
  },
  methods: {
    buildQueryString() {
      const params = new URLSearchParams();

      Object.entries(this.filters).forEach(([key, value]) => {
        if (String(value).trim() !== "") {
          params.append(key, value);
        }
      });

      params.append("sortKey0", "created_at");
      params.append("sortOrder0", "DESC");

      return params.toString();
    },
    async loadReports() {
      this.isLoading = true;

      try {
        const queryString = this.buildQueryString();
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/staff/get/loan/search?${queryString}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (response.ok) {
          const result = await response.json();
          this.reportItems = Array.isArray(result.data) ? result.data : [];
          this.currentPage = 1;
          this.selectedFaculty = "ทั้งหมด";
        } else if (response.status === 500) {
          throw new Error(await response.text());
        } else {
          throw new Error("Connection error");
        }
      } catch (error) {
        console.error(error.message);
        this.$refs.alertBox?.showAlert(error.message, "error");
      } finally {
        this.isLoading = false;
      }
    },
    async loadFacultyOptions() {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/user/loan/get/faculty`
        );

        if (response.ok) {
          const result = await response.json();
          this.facultyMasterList = Array.isArray(result)
            ? result
                .map((item) => item?.Faculty)
                .filter((faculty) => typeof faculty === "string" && faculty.trim() !== "")
            : [];
        } else if (response.status === 500) {
          throw new Error(await response.text());
        } else {
          throw new Error("Connection error");
        }
      } catch (error) {
        console.error(error.message);
        this.$refs.alertBox?.showAlert(error.message, "error");
      }
    },
    clearFilters() {
      this.filters = {
        student_id: "",
        first_name: "",
        last_name: "",
        graduated_level: "",
        study_year: "",
        study_semester: "",
        loan_status: "",
      };
      this.selectedFaculty = "ทั้งหมด";
      this.currentPage = 1;
      this.loadReports();
    },
    selectFaculty(faculty) {
      this.selectedFaculty = faculty;
      this.currentPage = 1;
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    statusClass(status) {
      switch (status) {
        case "อนุมัติ":
          return "status-pill--approved";
        case "ผ่านการตรวจสอบคุณสมบัติ":
          return "status-pill--success";
        case "อยู่ระหว่างดำเนินการ":
          return "status-pill--progress";
        case "อยู่ระหว่างตรวจสอบคุณสมบัติผู้กู้ยืม":
          return "status-pill--secondary";
        case "รอผลการพิจารณาจากกองทุน กยศ.":
          return "status-pill--warning";
        case "ตีกลับเพื่อแก้เอกสาร":
          return "status-pill--info";
        case "ไม่อนุมัติ":
          return "status-pill--danger";
        default:
          return "status-pill--default";
      }
    },
    openFullReport(publicId) {
      window.open(`/staff/info?id=${publicId}&ts=${Date.now()}`, "_blank");
    },
  },
  mounted() {
    this.loadFacultyOptions();
    this.loadReports();
  },
};
</script>

<style scoped>
.report-page {
  padding: 84px 16px 32px;
  background: linear-gradient(180deg, #f8f1f1 0%, #f7f7f7 40%, #ffffff 100%);
  min-height: 100vh;
}

.report-hero,
.report-panel {
  background: #ffffff;
  border-radius: 20px;
  margin-bottom: 1.25rem;
}

.report-hero {
  padding: 1.75rem;
  background: linear-gradient(135deg, #964040 0%, #b55f5f 100%);
  color: #ffffff;
}

.hero-eyebrow {
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
}

.hero-title {
  font-size: clamp(1.65rem, 3vw, 2.35rem);
  font-weight: 700;
}

.hero-subtitle {
  max-width: 860px;
  color: rgba(255, 255, 255, 0.9);
}

.hero-highlight {
  min-width: 220px;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(4px);
}

.hero-highlight__value {
  font-size: 2rem;
  font-weight: 700;
}

.hero-highlight__label {
  color: rgba(255, 255, 255, 0.85);
}

.report-panel {
  padding: 1.25rem;
}

.report-finance {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem 1rem;
  margin-top: 0.5rem;
}

.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #f4e4e4;
  color: #964040;
  padding: 0.45rem 0.9rem;
  font-weight: 600;
}

.btn-report-primary {
  background: #964040;
  border-color: #964040;
  color: #ffffff;
}

.btn-report-primary:hover,
.btn-report-primary:focus {
  background: #7e3434;
  border-color: #7e3434;
  color: #ffffff;
}

.btn-report-light {
  background: #f7ecec;
  border: 1px solid #e2c9c9;
  color: #964040;
}

.btn-report-light:hover,
.btn-report-light:focus {
  background: #f1dddd;
  color: #7e3434;
}

.stat-card {
  height: 100%;
  border-radius: 18px;
  background: #ffffff;
  padding: 1.2rem;
  border: 1px solid #efe3e3;
}

.stat-card__label {
  color: #6c757d;
  margin-bottom: 0.35rem;
}

.stat-card__value {
  font-size: 2rem;
  font-weight: 700;
  color: #964040;
  line-height: 1.1;
}

.stat-card__helper {
  margin-top: 0.35rem;
  color: #7b7b7b;
  font-size: 0.92rem;
}

.faculty-dropdown {
  min-width: min(100%, 320px);
}

.report-list {
  display: grid;
  gap: 1rem;
}

.report-card {
  border-radius: 18px;
  border: 1px solid #efe3e3;
  background: #ffffff;
  padding: 1.15rem;
}

.report-card__header,
.report-card__footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.report-card__view-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.report-card__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c2c2c;
}

.report-card__subtitle {
  color: #6c757d;
  font-size: 0.95rem;
}

.report-dropdown {
  padding: 0;
  overflow: hidden;
}

.report-dropdown__content {
  padding: 0 1.15rem 1.15rem;
}

.report-summary {
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  padding: 1rem 1.15rem;
}

.report-summary::-webkit-details-marker {
  display: none;
}

.report-summary__main {
  min-width: 0;
}

.report-summary__meta {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.35rem;
  color: #6c757d;
  font-size: 0.92rem;
}

.report-summary__faculty {
  color: #964040;
  font-weight: 600;
}

.report-summary__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.report-summary__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f7ecec;
  color: #964040;
}

.report-dropdown[open] .report-summary__icon i {
  transform: rotate(180deg);
}

.report-summary__icon i {
  transition: transform 0.2s ease;
}

.report-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem 1rem;
  margin: 1rem 0;
}

.report-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.report-field__label {
  color: #7b7b7b;
  font-size: 0.9rem;
}

.report-field__value {
  font-weight: 600;
  color: #212529;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.88rem;
}

.status-pill--approved,
.status-pill--success {
  background: #e8f7ee;
  color: #12844a;
}

.status-pill--progress {
  background: #fff3e3;
  color: #cc7a00;
}

.status-pill--secondary {
  background: #eceff3;
  color: #51606f;
}

.status-pill--warning {
  background: #fff7d6;
  color: #b28900;
}

.status-pill--info {
  background: #e8f0ff;
  color: #2f69c9;
}

.status-pill--danger {
  background: #ffe7e7;
  color: #c53434;
}

.status-pill--default {
  background: #f1f3f5;
  color: #5f6b76;
}

.loading-state,
.empty-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.empty-state i {
  font-size: 2.3rem;
  color: #964040;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.pagination-pages {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pagination-page {
  min-width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid #ead3d3;
  background: #ffffff;
  color: #964040;
  font-weight: 600;
}

.pagination-page--active {
  background: #964040;
  border-color: #964040;
  color: #ffffff;
}

@media (max-width: 991px) {
  .report-card__grid {
    grid-template-columns: 1fr;
  }

  .report-summary {
    align-items: flex-start;
  }
}

@media (max-width: 575px) {
  .report-page {
    padding: 78px 12px 24px;
  }

  .report-hero,
  .report-panel,
  .stat-card,
  .report-card {
    border-radius: 16px;
  }
}
</style>
